/**
 * Brief template mutations. `useCreateBriefTemplate` invalidates the list
 * cache and forwards outcomes to callers (callers own toast + nav).
 * `useUpdateBriefTemplate` invalidates detail + list and surfaces a
 * "Template saved" toast so every save gives identical feedback.
 */
import {
  BriefTemplatesService,
  type BriefTemplateLevel,
  type BriefTemplateResponseDto,
} from '@inference.engine/assets-api-client/web';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { assetsHttp } from '@/lib/api/http-client';
import { i18n } from '@/shared/i18n';
import { BRIEF_TEMPLATES_QUERY_KEYS } from './query-keys';

interface CreateBriefTemplateSectionVariables {
  name: string;
  position: number;
  required: boolean;
  aiAllowed: boolean;
  /** Per-section guidance shown next to the section name. Null = absent. */
  instructions: string | null;
}

export interface CreateBriefTemplateVariables {
  name: string;
  description?: string;
  sections?: CreateBriefTemplateSectionVariables[];
  /** Omitted creates a workspace template, which is all a caller could create before levels. */
  level?: Exclude<BriefTemplateLevel, 'system'>;
}

export interface UseCreateBriefTemplateOptions {
  onSuccess?: (created: BriefTemplateResponseDto) => void;
  onError?: (error: Error) => void;
}

export type UseCreateBriefTemplateReturn = UseMutationResult<
  BriefTemplateResponseDto,
  Error,
  CreateBriefTemplateVariables
>;

export function useCreateBriefTemplate(
  options?: UseCreateBriefTemplateOptions,
): UseCreateBriefTemplateReturn {
  const queryClient = useQueryClient();

  return useMutation<BriefTemplateResponseDto, Error, CreateBriefTemplateVariables>({
    mutationFn: async (variables) => {
      const response = await BriefTemplatesService.create({
        client: assetsHttp,
        body: {
          name: variables.name,
          ...(variables.description ? { description: variables.description } : {}),
          ...(variables.sections ? { sections: variables.sections } : {}),
          ...(variables.level ? { level: variables.level } : {}),
        },
      });

      if (!response.data) {
        throw new Error('Failed to create brief template');
      }

      return response.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: BRIEF_TEMPLATES_QUERY_KEYS.list(),
      });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

export interface UpdateBriefTemplateSectionVariables {
  /** Existing section id. Omit to insert a new section. */
  id?: string;
  name: string;
  position: number;
  required: boolean;
  aiAllowed: boolean;
  content?: Record<string, unknown>;
  /** Per-section guidance shown next to the section name. Null = absent. */
  instructions: string | null;
  type?: 'text' | 'date' | 'select' | 'checklist' | 'url' | 'file' | 'boolean';
  config?: Record<string, unknown>;
  allowFiles?: boolean;
  /** AI generation instructions. Null = not yet authored. */
  aiInstructions?: string | null;
}

export interface UpdateBriefTemplateVariables {
  name: string;
  description: string;
  /**
   * Sections are diffed by id (see `BriefTemplatesService.update`): a section carrying an id
   * is updated in place, one without is inserted, and a currently-live section whose id is
   * absent from the array is soft-deleted. Reorder travels as array order — `position` must
   * equal each section's array index.
   */
  sections?: UpdateBriefTemplateSectionVariables[];
}

export function useUpdateBriefTemplate(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: UpdateBriefTemplateVariables) => {
      const response = await BriefTemplatesService.update({
        client: assetsHttp,
        path: { id },
        body: {
          name: variables.name,
          description: variables.description,
          ...(variables.sections !== undefined ? { sections: variables.sections } : {}),
        },
      });

      if (!response.data) {
        throw new Error('Failed to update brief template');
      }

      return response.data;
    },
    onSuccess: (data) => {
      // The PATCH returns the same DTO the detail query does, so seeding the cache closes the
      // window where a refetch triggered here was still serving the pre-save template.
      queryClient.setQueryData(BRIEF_TEMPLATES_QUERY_KEYS.detail(id), data);
      void queryClient.invalidateQueries({
        queryKey: BRIEF_TEMPLATES_QUERY_KEYS.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: BRIEF_TEMPLATES_QUERY_KEYS.list(),
      });
      enqueueSnackbar(i18n.t('common:briefTemplate.mutations.templateSaved'), {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar(i18n.t('common:briefTemplate.mutations.failedToSaveTemplate'), {
        variant: 'error',
      });
    },
  });
}

export function usePublishBriefTemplate(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await BriefTemplatesService.publish({
        client: assetsHttp,
        path: { id },
      });

      if (!response.data) {
        throw new Error('Failed to publish brief template');
      }

      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: BRIEF_TEMPLATES_QUERY_KEYS.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: BRIEF_TEMPLATES_QUERY_KEYS.list(),
      });
      enqueueSnackbar(i18n.t('common:briefTemplate.mutations.templatePublished'), {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar(i18n.t('common:briefTemplate.mutations.failedToPublishTemplate'), {
        variant: 'error',
      });
    },
  });
}

export function useDeleteBriefTemplate(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await BriefTemplatesService.delete({
        client: assetsHttp,
        path: { id },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: BRIEF_TEMPLATES_QUERY_KEYS.list(),
      });
      enqueueSnackbar(i18n.t('common:briefTemplate.mutations.templateDeleted'), {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar(i18n.t('common:briefTemplate.mutations.failedToDeleteTemplate'), {
        variant: 'error',
      });
    },
  });
}

/**
 * Duplicate is atomic: fetch the source via `getById`, then create the copy
 * in a single `POST` carrying the full payload. `CreateBriefTemplateDto`
 * already accepts `description` + `sections`, so this avoids the orphaned
 * empty-template window a two-step create-then-update would leave behind.
 * `description` is coerced to a string (mirroring
 * `BriefTemplateBuilder.coerceDescription`) until the rich-text editor lands.
 */
export function useDuplicateBriefTemplate(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const originalResponse = await BriefTemplatesService.getById({
        client: assetsHttp,
        path: { id },
      });
      const original = originalResponse.data;
      if (!original) {
        throw new Error('Failed to load brief template');
      }

      const rawDescription = original.description;
      const descriptionString = typeof rawDescription === 'string' ? rawDescription : '';
      const sectionsPayload = original.sections.map((section) => ({
        name: section.name,
        position: section.position,
        required: section.required,
        aiAllowed: section.aiAllowed,
        instructions: section.instructions ?? null,
        ...(section.content !== undefined ? { content: section.content } : {}),
        type: section.type,
        config: section.config,
        allowFiles: section.allowFiles,
        // Omitted when absent, never sent as an explicit null. Every section authored before
        // typed sections reads back as `aiAllowed: true, aiInstructions: null`, and the server
        // rejects an explicit blank while AI is on — so sending the key duplicated a legacy
        // template straight into a 400. `toSectionDraft`'s payload builder omits it for the
        // same reason.
        ...(section.aiInstructions ? { aiInstructions: section.aiInstructions } : {}),
      }));

      const createdResponse = await BriefTemplatesService.create({
        client: assetsHttp,
        body: {
          name: i18n.t('common:briefTemplate.mutations.nameCopy', { name: original.name }),
          ...(descriptionString.length > 0 ? { description: descriptionString } : {}),
          ...(sectionsPayload.length > 0 ? { sections: sectionsPayload } : {}),
          ...(original.fields !== undefined ? { fields: original.fields } : {}),
          ...(original.briefTypes.length > 0 ? { briefTypes: original.briefTypes } : {}),
        },
      });
      const created = createdResponse.data;
      if (!created) {
        throw new Error('Failed to create brief template');
      }
      return created;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: BRIEF_TEMPLATES_QUERY_KEYS.list(),
      });
      enqueueSnackbar(i18n.t('common:briefTemplate.mutations.templateDuplicated'), {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar(i18n.t('common:briefTemplate.mutations.failedToDuplicateTemplate'), {
        variant: 'error',
      });
    },
  });
}
